import { ScrollArea } from '@ibsys/kit';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { VerificationStatus } from '@/entities/expert-report';
import { cn, NAVIGATION_TARGET_HIGHLIGHT_CLASS, useNavigationTarget } from '@/shared/lib';
import { buildEvaluationTargetKey, EVALUATION_TARGET_PARAM, useShownBlockScroll } from '../model';
import { EvaluationBlockFullViewButton } from './EvaluationBlockFullViewButton';
import { EvaluationBlockRatingHistory } from './EvaluationBlockRatingHistory';
import { EvaluationBlockResult } from './EvaluationBlockResult';
import { EvaluationRulesList } from './EvaluationRulesList';
import type { FlatRow } from './ExpertReportTab';
import { ExpertReportTotalRow } from './ExpertReportTotalRow';

type Props = {
  rows: FlatRow[];
  expertReportId: number;
  conversationId: number;
  /** Правила отобраны по результату — показаны не все */
  isFiltered: boolean;
  /** Стадия проверки результата человеком; отсутствует, пока результата нет */
  verificationStatus?: VerificationStatus;
  /**
   * Блоки, у результата которых есть версии, кроме действующей: историю версий результата строка
   * блока предлагает только им
   */
  ratingHistoryBlockIds: ReadonlySet<number>;
  /** Блок, раскрытый последним действием: к его наименованию прокручивается заключение */
  shownBlockId: number | null;
  /** Показывает блок целиком, а показанный — возвращает к отобранному виду */
  onToggleFullBlock: (blockId: number) => void;
};

const DEPTH_PADDING: Record<number, string> = { 0: 'pl-3', 1: 'pl-7', 2: 'pl-11', 3: 'pl-15' };
const getDepthPadding = (depth: number) => DEPTH_PADDING[depth] ?? 'pl-15';

/**
 * Отступы списка правил: слева меньше отступа наименования той же глубины на поле, которое правило
 * держит само под рамку выделения, — так текст правила встаёт на общую линию. Правое поле под ту же
 * рамку: иначе она упиралась бы в край заключения.
 */
const RULES_DEPTH_PADDING: Record<number, string> = { 0: 'pl-2', 1: 'pl-6', 2: 'pl-10', 3: 'pl-14' };
const getRulesDepthPadding = (depth: number) => `${RULES_DEPTH_PADDING[depth] ?? 'pl-14'} pr-1`;

/** Отступ балла от правого края заключения — парный к отступу наименования верхнего уровня слева. */
const RATING_RIGHT_PADDING = 'pr-3';

export const ExpertReportEvaluationTable: FC<Props> = ({
  rows,
  expertReportId,
  conversationId,
  isFiltered,
  verificationStatus,
  ratingHistoryBlockIds,
  shownBlockId,
  onToggleFullBlock,
}) => {
  const { t } = useTranslation(['dialogs']);
  const viewportRef = useRef<HTMLDivElement>(null);
  const evaluationTarget = useNavigationTarget(EVALUATION_TARGET_PARAM, viewportRef);
  const shownBlockScroll = useShownBlockScroll(shownBlockId, viewportRef);
  // Блоки в строках есть при любом отборе, поэтому пустота заключения видна по правилам
  const hasRules = rows.some((row) => row.rules && row.rules.length > 0);

  return (
    <div className="flex h-full flex-col">
      <ScrollArea className="min-h-0 flex-1" viewPortProps={{ ref: viewportRef }}>
        <div className="flex flex-col text-sm">
          {rows.map((row) => {
            const isTargetBlock =
              !row.isTotal && evaluationTarget.targetKey === buildEvaluationTargetKey('block', row.blockId);

            return (
              <div
                key={row.id}
                ref={(element) => {
                  if (isTargetBlock) evaluationTarget.attachTarget(element);
                  // Реестру нужны только строки блоков: итог блоком не является, а ключ `blockId: 0`
                  // занял бы в нём место блока, если такой идентификатор придёт с бэка
                  if (!row.isTotal) shownBlockScroll.attachRow(row, element);
                }}
                className={cn(
                  'border-b py-2',
                  row.isTotal && 'font-bold',
                  row.isParent && 'bg-secondary font-bold',
                  isTargetBlock && NAVIGATION_TARGET_HIGHLIGHT_CLASS,
                )}
              >
                {row.isTotal ? (
                  <ExpertReportTotalRow
                    row={row}
                    conversationId={conversationId}
                    expertReportId={expertReportId}
                    verificationStatus={verificationStatus}
                    className={cn(getDepthPadding(0), RATING_RIGHT_PADDING)}
                  />
                ) : (
                  <div className={cn('flex items-center gap-2', getDepthPadding(row.depth), RATING_RIGHT_PADDING)}>
                    <span className="flex flex-1 items-center gap-1">
                      {row.name}
                      {row.canShowFull && (
                        <EvaluationBlockFullViewButton
                          isShownFull={row.isShownFull}
                          onToggle={() => onToggleFullBlock(row.blockId)}
                        />
                      )}
                    </span>
                    {/* История результата — только у листового блока: у блока с вложенными результат
                        складывается по правилу расчёта и не правится */}
                    {!row.isParent && ratingHistoryBlockIds.has(row.blockId) && (
                      <EvaluationBlockRatingHistory
                        conversationId={conversationId}
                        expertReportId={expertReportId}
                        blockId={row.blockId}
                      />
                    )}
                    <EvaluationBlockResult row={row} conversationId={conversationId} expertReportId={expertReportId} />
                  </div>
                )}
                {row.rules && row.rules.length > 0 && (
                  <div className={cn('mt-1 text-muted-foreground', getRulesDepthPadding(Math.min(row.depth + 1, 3)))}>
                    <EvaluationRulesList
                      rules={row.rules}
                      conversationId={conversationId}
                      expertReportId={expertReportId}
                      blockId={row.blockId}
                      evaluationTarget={evaluationTarget}
                    />
                  </div>
                )}
              </div>
            );
          })}
          {isFiltered && !hasRules && (
            <div className={cn('text-muted-foreground py-2', getDepthPadding(0))}>
              {t('expertReport.noPickedRules')}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
